import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Okrcategary from './okr-categary';
import { OkrPopUp } from '../../types/okrpopup.types';
import * as OkrPopAction from '../../actions/okr_popup.action';
import KrService from '../../services/kr.service';
import { tabListHelper } from '../../../utils/tabList';
import { FinancialsService, FinancialsObjectType } from '../../../apps/financials';
import { useTabOverflow } from './useTabOverflow';

const service = new KrService();
const refID = 'okr-goal-tabs-panel-container';

const OkrTab = ({ pathInfo, OkrActiveTab, goalId, cacheKeySet, dispatch, ...rest }: OkrPopUp) => {
    const { t } = useTranslation();
    const { goalData } = { ...rest };
    const [data, setData] = useState<any[]>([]);
    const [moreOpen, setMoreOpen] = useState(false);
    const moreWrapperRef = useRef<HTMLDivElement>(null);

    const {
        tabRowRef, categoryWrapRef, measureRefs, moreBtnRef,
        visibleIndices, overflowIndices, hasOverflow,
    } = useTabOverflow(data, OkrActiveTab);

    useEffect(() => {
        const isFinancialsEnabled = (window as any).wbConfig?.isFinancialsEnabled;
        const financialsService = new FinancialsService();

        const fetchData = async () => {
            try {
                // Fetch tabs from backend
                const tabsResponse = await service.getOkrPopUpTabs({
                    id: goalId,
                    selectTabs: goalData?.selectTabs ?? null,
                });

                let tabs = tabsResponse.data.data || [];

                // Check if financials exist for this objective (only when feature flag is enabled)
                if (isFinancialsEnabled) {
                    const financialsResponse = await financialsService.getEntityByObject({
                        objectType: FinancialsObjectType.OKR,
                        objectId: Number(goalId),
                    });

                    // If financials exist, add the Financials tab
                    if (financialsResponse?.data?.financial?.id) {
                        tabs = [...tabs, {
                            id: 'financials',
                            text: t('Financials'),
                            iconIn: 'icon-financials-18px-blue',
                            iconOut: 'icon-financials-18px',
                            active: false,
                        }];
                    }
                }

                setData(tabs);
                setMoreOpen(false);
                dispatch(OkrPopAction.UpdateActiveMenus(tabs));
            } catch {
                // handled
            }
        };

        fetchData();
        service.setGoalDetailsEvent({ pathInfo, goalId, cacheKeySet })
            .then(() => {}).catch(() => {});
        // eslint-disable-next-line
    }, [goalId, goalData?.reload]);

    useEffect(() => {
        if (!moreOpen) return;
        const handleOutsideClick = (e: MouseEvent) => {
            if (moreWrapperRef.current && !moreWrapperRef.current.contains(e.target as Node)) {
                setMoreOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [moreOpen]);

    const tabClick = (item: any) => dispatch(OkrPopAction.UpdateActiveTab(item.text));

    const tabSlug = (text: string) => text === t('Key Results') ? 'key-results' : text;

    const renderTabContent = (d: any) => {
        const isActive = d.text === OkrActiveTab;
        return (
            <>
                {d.text === t('Custom Fields') ? (
                    <svg width="16" height="10" viewBox="0 0 16 10" className="custom_svg" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.5 10H10.5V8.33333H0.5V10ZM0.5 0V1.66667H15.5V0H0.5ZM0.5 5.83333H15.5V4.16667H0.5V5.83333Z" fill={d.active ? '#71c6e5' : '#bcbcbc'} />
                    </svg>
                ) : (
                    <span
                        className={`icon ${d.text === 'Notes' ? d.icon : (isActive ? d.iconIn : d.iconOut)}`}
                        aria-hidden="true"
                        data-in={d.iconIn}
                        data-out={d.iconOut}
                    >
                        &nbsp;
                    </span>
                )}
                <span>{tabSlug(d.text) === 'key-results' ? t('Key Results') : d.text}</span>
            </>
        );
    };

    return (
        <div className="goal-details-sub-header goal-details-tabs">
            {/* Off-screen clone of all tabs — used for width measurement only */}
            <div className="okr-tabs-measure" aria-hidden="true">
                {data.map((d, i) => (
                    <div key={d.text} className="wb-ghead" ref={el => { measureRefs.current[i] = el; }}>
                        {renderTabContent(d)}
                    </div>
                ))}
            </div>

            <div className="okr-tabs-row" role="tablist" id={refID} aria-label="Objective navigation" ref={tabRowRef}>
                {visibleIndices.map(dataIdx => {
                    const d = data[dataIdx];
                    if (!d) return null;
                    const isActive = d.text === OkrActiveTab;
                    const slug = tabSlug(d.text);
                    return (
                        <div
                            key={d.text}
                            role="tab"
                            aria-selected={isActive}
                            aria-controls={`active-tab-${slug}`}
                            id={`okrActiveTab-${slug}`}
                            className={`wb-ghead${isActive ? ' active' : ' '}`}
                            data-id={d.id}
                            tabIndex={isActive ? 0 : -1}
                            onClick={() => tabClick(d)}
                            onKeyDown={e => tabListHelper(refID, () => tabClick(d), e)}
                        >
                            {renderTabContent(d)}
                        </div>
                    );
                })}

                {/* Always mounted so moreBtnRef has a measurable width before the first overflow check */}
                <div ref={moreWrapperRef} className={`okr-tabs-more-wrapper${hasOverflow ? '' : ' okr-tabs-more-wrapper--hidden'}`}>
                    <button
                        ref={moreBtnRef}
                        className={`okr-tabs-more-btn${moreOpen ? ' open' : ''}`}
                        aria-haspopup="listbox"
                        aria-expanded={moreOpen}
                        tabIndex={hasOverflow ? 0 : -1}
                        onClick={() => setMoreOpen(o => !o)}
                    >
                        {t('More')} <span className="okr-tabs-more-chevron" aria-hidden="true" />
                    </button>
                    {moreOpen && (
                        <div className="okr-tabs-more-dropdown" role="listbox">
                            {overflowIndices.map(dataIdx => {
                                const d = data[dataIdx];
                                if (!d) return null;
                                return (
                                    <div
                                        key={d.text}
                                        role="option"
                                        tabIndex={0}
                                        className="okr-tabs-more-item"
                                        onClick={() => { setMoreOpen(false); tabClick(d); }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setMoreOpen(false);
                                                tabClick(d);
                                            }
                                        }}
                                    >
                                        {tabSlug(d.text) === 'key-results' ? t('Key Results') : d.text}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div ref={categoryWrapRef} className="okr-tabs-category-wrapper">
                <Okrcategary goalId={goalId} />
            </div>
        </div>
    );
};

export default OkrTab;
